import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '../../../providers/ToastProvider.js';
import { setCasePriority } from '../../../services/workQueue.js';
import { invalidateWorkQueue } from '../util.js';
import type { PriorityLevel } from './priority.js';

interface UsePrioritySetterResult {
    isSubmitting: boolean;
    /** Resolves true on success, false on failure — callers revert optimistic UI on false. */
    setPriority: (patientId: string, priority: PriorityLevel, reason?: string) => Promise<boolean>;
}

export function usePrioritySetter(): UsePrioritySetterResult {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const queryClient = useQueryClient();
    const { showToast } = useToast();

    const setPriority = async (
        patientId: string,
        priority: PriorityLevel,
        reason?: string
    ): Promise<boolean> => {
        setIsSubmitting(true);
        try {
            await setCasePriority(patientId, priority, reason);
            await invalidateWorkQueue(queryClient);
            await queryClient.invalidateQueries({ queryKey: ['patient-timeline', patientId] });
            return true;
        } catch {
            // A failed write leaves the server value unchanged, so the refetch above
            // never fires — the caller must revert its optimistic override itself.
            showToast('Failed to update priority. Please try again.', 'error');
            return false;
        } finally {
            setIsSubmitting(false);
        }
    };

    return { isSubmitting, setPriority };
}
